import React, { useEffect, useRef, useState } from 'react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import '../styles/datepicker.css'

// Robot orange — used for the "Clear due date" action so it visually matches
// the b4igo brand orange used elsewhere (e.g. orange-robot-bold.svg, $orange).
const ROBOT_ORANGE = '#fd7e14'

interface ChecklistDueDatePickerProps {
  // Anchor rect of the clock button that opened the picker (viewport coords).
  anchorRect: { top: number; bottom: number; left: number; right: number }
  // Pre-fill from the item's existing data-checklist-due-date / -due-time.
  // null → user hasn't set one yet (no Clear button shown).
  initial: Date | null
  onSave: (date: Date) => void
  onClear: () => void
  onCancel: () => void
  theme?: string
}

// Calendar is ~280px and the time list is ~85px — together they need ~380px
// of horizontal room to render side by side without the time list wrapping
// to a new line below.
const POPOVER_WIDTH = 400

const ChecklistDueDatePicker: React.FC<ChecklistDueDatePickerProps> = ({
  anchorRect,
  initial,
  onSave,
  onClear,
  onCancel,
  theme,
}) => {
  const [date, setDate] = useState<Date | null>(initial ?? null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      if (!containerRef.current) return
      if (containerRef.current.contains(e.target as Node)) return
      onCancel()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel()
    }
    document.addEventListener('mousedown', onDocMouseDown, true)
    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown, true)
      document.removeEventListener('keydown', onKey, true)
    }
  }, [onCancel])

  // Position the popover under the clock button, clamped to the viewport so
  // it never spills off-screen on items near the right edge.
  const top = Math.min(anchorRect.bottom + 4, window.innerHeight - 320)
  const left = Math.max(8, Math.min(anchorRect.left, window.innerWidth - POPOVER_WIDTH - 8))

  return (
    <div
      ref={containerRef}
      className={`ql-checklist-duedate-popover ${theme === 'dark' ? 'dark react_date_dark' : ''}`}
      style={{ position: 'fixed', top, left, width: POPOVER_WIDTH, zIndex: 1080 }}
    >
      <div className="ql-checklist-duedate-title">Due date &amp; time</div>
      <DatePicker
        inline
        selected={date}
        onChange={(d: Date | null) => setDate(d)}
        showTimeSelect
        timeFormat="h:mm aa"
        dateFormat="MM/dd/yyyy h:mm aa"
        minDate={new Date()}
      />
      <div className="ql-checklist-duedate-actions">
        {initial && (
          <button
            type="button"
            className="ql-checklist-duedate-clear"
            style={{ color: ROBOT_ORANGE }}
            onClick={() => onClear()}
          >
            Clear due date
          </button>
        )}
        <div className="ql-checklist-duedate-spacer" />
        <button
          type="button"
          className="ql-checklist-duedate-cancel"
          onClick={() => onCancel()}
        >
          Cancel
        </button>
        <button
          type="button"
          className="ql-checklist-duedate-save"
          disabled={!date}
          onClick={() => date && onSave(date)}
        >
          Save
        </button>
      </div>
    </div>
  )
}

export default ChecklistDueDatePicker
